import { useState } from 'react'
import { Box, Typography, TextField, IconButton, InputAdornment, Link } from '@mui/material'
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material'
import { designSpecs } from '../../theme/designSpecs'
import Button from '../common/Button'

const PasswordStep = ({ email, onSubmit, onBack, defaultMode = 'signup', error: apiError }) => {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSignUp, setIsSignUp] = useState(defaultMode === 'signup')
  const [error, setError] = useState('')

  const handleSubmit = () => {
    if (!password.trim()) {
      setError('Please enter your password')
      return
    }

    if (isSignUp && password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setError('')
    onSubmit(password, isSignUp)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  return (
    <Box>
      <Button
        variant="text"
        startIcon={<ArrowBack />}
        onClick={onBack}
        sx={{
          mb: 2,
          color: 'grey.600',
          '&:hover': {
            bgcolor: 'grey.100',
          },
        }}
      >
        Back
      </Button>

      <Typography
        variant="h3"
        sx={{
          mb: 1,
          fontWeight: 700,
          color: 'grey.900',
        }}
      >
        {isSignUp ? 'Create your account' : 'Welcome back'}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mb: 1,
          color: 'grey.600',
        }}
      >
        {email}
      </Typography>
      <Link
        component="button"
        onClick={onBack}
        sx={{
          fontSize: designSpecs.typography.sizes.sm,
          color: 'primary.main',
          mb: 3,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        Change email
      </Link>

      <TextField
        fullWidth
        type={showPassword ? 'text' : 'password'}
        placeholder={isSignUp ? 'Create password' : 'Enter password'}
        value={password}
        onChange={(e) => {
          setPassword(e.target.value)
          setError('')
        }}
        onKeyPress={handleKeyPress}
        error={!!(error || apiError)}
        helperText={error || apiError}
        autoFocus
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                sx={{ color: 'grey.500' }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            fontSize: designSpecs.typography.sizes.base,
            borderRadius: designSpecs.borderRadius.lg,
            '& fieldset': {
              borderWidth: 2,
              borderColor: (error || apiError) ? 'error.main' : 'grey.300',
            },
            '&:hover fieldset': {
              borderColor: (error || apiError) ? 'error.main' : 'grey.400',
            },
            '&.Mui-focused fieldset': {
              borderColor: (error || apiError) ? 'error.main' : 'primary.main',
              borderWidth: 2,
            },
          },
          '& .MuiFormHelperText-root': {
            color: 'error.main',
            fontWeight: 500,
          },
        }}
      />

      {!isSignUp && (
        <Box sx={{ textAlign: 'right', mb: 3 }}>
          <Link
            component="button"
            sx={{
              fontSize: designSpecs.typography.sizes.sm,
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Forgot password?
          </Link>
        </Box>
      )}

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handleSubmit}
        sx={{
          background: designSpecs.gradients.primary,
          py: 1.5,
          fontWeight: 600,
          mb: 2,
          '&:hover': {
            background: designSpecs.gradients.primary,
          },
        }}
      >
        {isSignUp ? 'Sign up' : 'Sign in'}
      </Button>

      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          color: 'grey.600',
        }}
      >
        {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
        <Link
          component="button"
          onClick={() => {
            setIsSignUp(!isSignUp)
            setError('')
          }}
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {isSignUp ? 'Sign in' : 'Sign up'}
        </Link>
      </Typography>
    </Box>
  )
}

export default PasswordStep
